import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { EduzyLogo } from "@/components/ui/EduzyLogo";
import { ProgressRing, StreakFire } from "@/components/ui/animations";
import { PracticeTestCard } from "@/components/student/PracticeTestCard";
import { LearningPathCard } from "@/components/student/LearningPathCard";
import { UpgradeModal } from "@/components/student/UpgradeModal";
import { LogoutModal as PortalLogoutModal } from "@/components/PortalLayout";
import SharedFooter from "@/components/SharedFooter";
import UnifiedPortalHeader from "@/components/UnifiedPortalHeader";
import { StudentStatsBar } from "@/components/PortalStatsBar";
import {
  GraduationCap,
  User,
  Mail,
  Clock,
  Award,
  Target,
  LogOut,
  CheckCircle2,
  TrendingUp,
  History,
  Trophy,
  ClipboardList,
  Calendar,
  Medal,
  Flame,
  Star,
  Zap,
  MessageCircle,
  Play,
  Sparkles,
  ChevronRight,
  Map,
  Lightbulb,
  Home,
  Camera,
  Upload,
} from "lucide-react";

export default function StudentProfile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("generic");
  const [profilePicture, setProfilePicture] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Handle browser back button - show logout confirmation
  useEffect(() => {
    const handlePopState = (event) => {
      // Prevent default navigation and show logout modal
      event.preventDefault();
      // Push current state back to prevent navigation
      window.history.pushState(null, '', window.location.href);
      setShowLogoutModal(true);
    };

    // Push initial state so we can detect back button
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        
        // Fetch profile picture
        if (data.user?.student_id) {
          try {
            const picResponse = await fetch(`${API}/auth/profile-picture/${data.user.student_id}`, {
              credentials: "include",
            });
            if (picResponse.ok) {
              const picData = await picResponse.json();
              if (picData.success && picData.profile_picture) {
                setProfilePicture(picData.profile_picture);
              }
            }
          } catch (e) {
            // Silently ignore profile picture fetch errors
          }
        }
      } else if (response.status === 401) {
        navigate("/student/login");
      }
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPEG, PNG, or WebP images.");
      return;
    }

    // Check file size (2 MB max)
    const maxSize = 2 * 1024 * 1024; // 2 MB
    if (file.size > maxSize) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(2)} MB). Maximum size is 2 MB.`);
      return;
    }

    setUploadingPicture(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;
        
        try {
          const response = await fetch(`${API}/auth/profile-picture/upload`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              image_data: base64Data,
              student_id: user.student_id,
            }),
          });

          const data = await response.json();

          if (response.ok && data.success) {
            setProfilePicture(base64Data);
            toast.success("Profile picture uploaded successfully!");
          } else {
            toast.error(data.detail || "Failed to upload profile picture");
          }
        } catch (err) {
          toast.error("Failed to upload profile picture");
        } finally {
          setUploadingPicture(false);
        }
      };
      reader.onerror = () => {
        toast.error("Failed to read file");
        setUploadingPicture(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Failed to process image");
      setUploadingPicture(false);
    }

    // Reset input
    event.target.value = '';
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      toast.success("Logged out successfully");
      setShowLogoutModal(false);
      navigate("/");
    } catch (error) {
      toast.error("Logout failed");
      setShowLogoutModal(false);
    }
  };
  
  const confirmLogout = () => {
    setShowLogoutModal(true);
  };

  const handleUpgradeClick = (reason = "generic") => {
    setUpgradeReason(reason);
    setShowUpgradeModal(true);
  };

  // Get subscription plan type
  const planType = user?.subscription?.plan_type || "starter";

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const accuracy = user.accuracy || 0;
  const totalQuestions = user.total_questions || 0;
  const correctAnswers = user.correct_answers || 0;
  const currentStreak = user.current_streak || 0;
  const longestStreak = user.longest_streak || 0;
  const totalXP = user.xp_points || 0; // Use actual XP from database
  
  // Calculate level from XP (level up every 100 XP)
  // Level 1: 0-99 XP, Level 2: 100-199 XP, etc.
  const currentLevel = Math.floor(totalXP / 100) + 1;
  const xpInCurrentLevel = totalXP % 100;
  const xpToNextLevel = 100 - xpInCurrentLevel;
  const levelProgress = (xpInCurrentLevel / 100) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex flex-col">
      {/* Unified Header matching landing page - without user info */}
      <UnifiedPortalHeader
        portalType="student"
        userName=""
        userSubtitle=""
        onLogout={confirmLogout}
        showUserInfo={false}
        largeLogout={true}
        showHomeButton={false}
      />

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6 flex-1">
        {/* Hidden file input for profile picture upload */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          data-testid="profile-picture-input"
        />

        {/* Merged Student Card - Info + Stats + Actions */}
        <Card className="bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-400 text-white border-0 shadow-xl overflow-hidden">
          <CardContent className="p-6">
            {/* Top Section: Avatar + Name + Grade */}
            <div className="flex items-center gap-5 mb-6">
              {/* Profile Picture with Upload */}
              <div 
                className="cursor-pointer"
                onClick={handleProfilePictureClick}
                data-testid="profile-picture-container"
              >
                <div className="w-28 h-28 bg-white/20 backdrop-blur-sm rounded-2xl flex flex-col items-center justify-center border-2 border-white/30 overflow-hidden transition-all hover:border-white/60 hover:shadow-lg hover:bg-white/30">
                  {profilePicture ? (
                    <img 
                      src={profilePicture} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : uploadingPicture ? (
                    <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Camera className="w-8 h-8 mb-1" />
                      <span className="text-sm font-medium">Upload</span>
                    </>
                  )}
                </div>
                {/* Size and resolution info - always visible when no image */}
                {!profilePicture && (
                  <p className="text-[11px] text-white/80 text-center mt-2 leading-tight">
                    2MB max • 400×400px
                  </p>
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold" data-testid="student-name">{user.name}</h2>
                <div className="flex items-center gap-3 mt-1">
                  <Badge className="bg-white/20 text-white border-0 hover:bg-white/30">
                    <GraduationCap className="w-3 h-3 mr-1" />
                    Grade {user.grade}
                  </Badge>
                  <span className="text-emerald-100 text-sm font-mono" data-testid="student-id">{user.student_id}</span>
                </div>
              </div>
            </div>

            {/* Stats Row: Level, Streak, XP, Accuracy */}
            <div className="grid grid-cols-4 gap-3 mb-6">
              {/* Level - Gold/Yellow */}
              <div className="bg-gradient-to-br from-yellow-400/50 to-amber-500/40 backdrop-blur-sm rounded-xl p-3 text-center border border-yellow-300/50">
                <div className="w-10 h-10 bg-yellow-400/50 rounded-full flex items-center justify-center mx-auto mb-1">
                  <Star className="w-5 h-5 text-yellow-100" />
                </div>
                <p className="text-2xl font-bold">{currentLevel}</p>
                <p className="text-xs text-white/90">Level</p>
              </div>

              {/* Streak - Orange/Red */}
              <div className="bg-gradient-to-br from-orange-400/50 to-red-500/40 backdrop-blur-sm rounded-xl p-3 text-center border border-orange-300/50">
                <div className="w-10 h-10 bg-orange-400/50 rounded-full flex items-center justify-center mx-auto mb-1">
                  <Flame className="w-5 h-5 text-orange-100" />
                </div>
                <p className="text-2xl font-bold">{currentStreak}</p>
                <p className="text-xs text-white/90">Day Streak</p>
              </div>

              {/* XP - Purple/Pink */}
              <div className="bg-gradient-to-br from-purple-400/50 to-pink-500/40 backdrop-blur-sm rounded-xl p-3 text-center border border-purple-300/50">
                <div className="w-10 h-10 bg-purple-400/50 rounded-full flex items-center justify-center mx-auto mb-1">
                  <Zap className="w-5 h-5 text-purple-100" />
                </div>
                <p className="text-2xl font-bold">{totalXP}</p>
                <p className="text-xs text-white/90">Total XP</p>
              </div>

              {/* Accuracy - Blue/Cyan */}
              <div className="bg-gradient-to-br from-blue-400/50 to-cyan-500/40 backdrop-blur-sm rounded-xl p-3 text-center border border-blue-300/50">
                <div className="w-10 h-10 bg-blue-400/50 rounded-full flex items-center justify-center mx-auto mb-1">
                  <Target className="w-5 h-5 text-blue-100" />
                </div>
                <p className="text-2xl font-bold">{accuracy.toFixed(0)}%</p>
                <p className="text-xs text-white/90">Accuracy</p>
              </div>
            </div>

            {/* XP Progress Bar */}
            <div className="mb-6">
              <div className="bg-white/20 rounded-full h-2.5 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-yellow-400 to-amber-400 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${levelProgress}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-emerald-100 mt-1.5">
                <span>{xpInCurrentLevel} / 100 XP</span>
                <span>{xpToNextLevel} XP to Level {currentLevel + 1}</span>
              </div>
            </div>

            {/* Action Buttons: Start Learning + Homework Helper */}
            <div className="grid grid-cols-2 gap-3">
              <Button 
                className="h-14 text-base bg-white text-emerald-700 hover:bg-emerald-50 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] font-semibold"
                onClick={() => navigate("/student/learn")}
                data-testid="start-learning-main-btn"
              >
                <Sparkles className="h-5 w-5 mr-2 text-emerald-600" />
                <span className="hidden sm:inline">Start Learning</span>
                <span className="sm:hidden">Learn</span>
              </Button>

              <Button 
                className="h-14 text-base bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 border-2 border-white/30 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] font-semibold"
                onClick={() => navigate("/student/learn?mode=homework")}
                data-testid="homework-helper-profile-btn"
              >
                <Lightbulb className="h-5 w-5 mr-2" />
                <span className="hidden sm:inline">Homework Helper</span>
                <span className="sm:hidden">Homework</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-md bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900" data-testid="total-questions">{totalQuestions}</p>
                  <p className="text-xs text-gray-500">Questions</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900" data-testid="correct-answers">{correctAnswers}</p>
                  <p className="text-xs text-gray-500">Correct</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md bg-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Flame className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900" data-testid="best-streak">{longestStreak}</p>
                  <p className="text-xs text-gray-500">Best Streak</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md bg-purple-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">#{Math.floor(Math.random() * 50) + 1}</p>
                  <p className="text-xs text-gray-500">Rank</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Practice Tests & Learning Path Section */}
        <div className="grid md:grid-cols-2 gap-4">
          <PracticeTestCard 
            planType={planType}
            onUpgradeClick={() => handleUpgradeClick("practice_tests")}
          />
          <LearningPathCard
            studentId={user?.student_id}
            grade={user?.grade || 5}
            subject="math"
            planType={planType}
            onUpgradeClick={() => handleUpgradeClick("learning_path")}
          />
        </div>

        {/* Premium Features Section */}
        <Card className="border-0 shadow-md bg-gradient-to-r from-violet-100 via-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-violet-600" />
              Premium Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Button
                variant="outline"
                className="h-auto py-4 flex-col gap-2 bg-violet-50/80 border-violet-200 hover:bg-violet-100 hover:border-violet-300"
                onClick={() => navigate("/student/practice-tests")}
                data-testid="practice-tests-btn"
              >
                <ClipboardList className="w-6 h-6 text-violet-600" />
                <span className="text-sm font-medium">Practice Tests</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto py-4 flex-col gap-2 bg-pink-50/80 border-pink-200 hover:bg-pink-100 hover:border-pink-300"
                onClick={() => navigate("/student/study-plans")}
                data-testid="study-plans-btn"
              >
                <Calendar className="w-6 h-6 text-pink-600" />
                <span className="text-sm font-medium">Study Plans</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto py-4 flex-col gap-2 bg-amber-50/80 border-amber-200 hover:bg-amber-100 hover:border-amber-300"
                onClick={() => navigate("/student/certificates")}
                data-testid="certificates-btn"
              >
                <Medal className="w-6 h-6 text-amber-600" />
                <span className="text-sm font-medium">Certificates</span>
              </Button>
              <Button
                variant="outline"
                className="h-auto py-4 flex-col gap-2 bg-teal-50/80 border-teal-200 hover:bg-teal-100 hover:border-teal-300"
                onClick={() => navigate("/student/leaderboard")}
                data-testid="leaderboard-card-btn"
              >
                <Trophy className="w-6 h-6 text-teal-600" />
                <span className="text-sm font-medium">Leaderboard</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* View History Quick Access */}
        <Button 
          variant="outline" 
          className="w-full h-12 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
          onClick={() => navigate("/student/history")}
          data-testid="view-history-btn"
        >
          <History className="w-4 h-4 mr-2" />
          View History
        </Button>
      </main>

      {/* Logout Confirmation Modal - Using Portal Styled Modal */}
      <PortalLogoutModal
        open={showLogoutModal}
        onOpenChange={setShowLogoutModal}
        onConfirm={handleLogout}
        portalType="student"
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        reason={upgradeReason}
        currentPlan={planType}
      />

      {/* Unified Footer matching landing page */}
      <SharedFooter />
    </div>
  );
}
